import * as React from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { loadLocaleAsync, loadNamespaceAsync } from "@i18n/i18n-util.async";
import type { Locales } from "@i18n/i18n-types";
import { Button } from "@shadcn/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn/ui/select";
import { Label } from "@shadcn/ui/label";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";

export const Route = createFileRoute("/profile/change-app-language")({
	beforeLoad: () => {
		document.title = 'bacehub | Startseite'
	},
	component: RouteComponent,
});

const languages = [
	{ value: "en" as Locales, label: "English", flag: "🇬🇧" },
	{ value: "de" as Locales, label: "Deutsch", flag: "🇩🇪" },
];

export default function RouteComponent() {
	const { locale } = useI18nContext();
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const [selectedLocale, setSelectedLocale] = React.useState<Locales>(locale);

	const handleLanguageChange = async (newLocale: Locales) => {
		console.log("Changing language to:", newLocale);
		localStorage.setItem("locale", newLocale);

		try {
			await loadLocaleAsync(newLocale);

			// Load namespaces for new-ui app
			await Promise.all([
				loadNamespaceAsync(newLocale, "new-ui"),
				loadNamespaceAsync(newLocale, "auth"),
				loadNamespaceAsync(newLocale, "shared"),
			]);

			console.log("Language changed, reloading page...");
			window.location.reload();
		} catch (error) {
			console.error("Failed to load locale:", error);
		}
	};

	return (
		<PageContainer>
			<AppHeader service="ALL" />
			<div className="flex flex-col min-h-[calc(100vh-4rem)]">
				<div className="flex-1">
					<h1 className="text-2xl font-breit px-6 py-4">
						<T>{i18n.PROFILE_CHANGE_APP_LANGUAGE()}</T>
					</h1>
					<div className="px-6">
						<div className="flex flex-col items-start gap-2 w-full">
							<Label htmlFor="language-select">{locale === "de" ? "Sprache wählen" : "Choose Language"}</Label>
							<Select value={selectedLocale} onValueChange={(value) => setSelectedLocale(value as Locales)}>
								<SelectTrigger className="w-full" id="language-select">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{languages.map((language) => (
										<SelectItem key={language.value} value={language.value}>
											<span className="flex items-center gap-2">
												<span>{language.flag}</span>
												<span>{language.label}</span>
											</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</div>
				<div className="flex flex-col items-center justify-end gap-4 p-6">
					<Button size="lg" className="w-full" onClick={() => handleLanguageChange(selectedLocale)}>
						<T>{i18n.CONFIRM()}</T>
					</Button>
					<Button variant="ghost" size="lg" className="w-full" onClick={() => router.history.back()}>
						<T>{i18n.CANCEL()}</T>
					</Button>
				</div>
			</div>
		</PageContainer>
	);
}
